import { useState, useEffect, useRef } from "react";
import { useReducedMotion } from "@/hooks/useReducedMotion";

// Seeded PRNG for deterministic SSR/client output (avoids hydration mismatch)
function seededRandom(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 16807 + 0) % 2147483647;
    return (s - 1) / 2147483646;
  };
}
const rand = seededRandom(42);

const AGENTS = ["Product", "Design", "Architect", "Dev", "DevOps", "QA"];

const ACTIONS = [
  "User flow spec updated",
  "Component library refreshed",
  "API schema validated",
  "Auth module refactored",
  "CI pipeline optimised",
  "Regression suite passed",
  "Feature flag configured",
  "Design tokens synced",
  "Database migration tested",
  "Endpoint load tested",
  "Container image rebuilt",
  "Integration tests green",
  "Roadmap priorities updated",
  "Responsive layout reviewed",
  "Event bus schema versioned",
  "Cache layer deployed",
  "Monitoring dashboard updated",
  "Acceptance criteria verified",
];

function padTime(offset: number): string {
  const h = ((24 - offset * 3 + Math.floor(rand() * 2)) % 24 + 24) % 24;
  const m = Math.floor(rand() * 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function generateEntry(index: number) {
  return {
    time: padTime(index),
    agent: AGENTS[index % AGENTS.length]!,
    action: ACTIONS[index % ACTIONS.length]!,
    id: index,
  };
}

const INITIAL_ENTRIES = Array.from({ length: 6 }, (_, i) => generateEntry(i));

// GitHub-style grid: generate enough data for max weeks, render based on container width
const MAX_WEEKS = 52;
const DAYS = 7;
const CELL_SIZE = 10;
const CELL_GAP = 2;

// Generate cell data with intensity, task count, and date
const TODAY = new Date("2026-03-25");
const GRID_CELLS = Array.from({ length: MAX_WEEKS * DAYS }, (_, i) => {
  const recency = i / (MAX_WEEKS * DAYS);
  const intensity = Math.min(1, 0.15 + recency * 0.55 + rand() * 0.3);
  const tasks = Math.round(intensity * 18 + rand() * 6);
  // Calculate date: cell 0 is (MAX_WEEKS * 7) days ago, last cell is today
  const daysAgo = (MAX_WEEKS * DAYS - 1) - i;
  const date = new Date(TODAY);
  date.setDate(date.getDate() - daysAgo);
  const dateStr = date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  return { intensity, tasks, dateStr };
});

export function AgentActivityFeed() {
  const prefersReducedMotion = useReducedMotion();
  const [entries, setEntries] = useState(INITIAL_ENTRIES);
  const counterRef = useRef(INITIAL_ENTRIES.length);
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const [visibleWeeks, setVisibleWeeks] = useState(MAX_WEEKS);

  // Calculate how many weeks fit in the container
  useEffect(() => {
    const el = gridContainerRef.current;
    if (!el) return;
    const measure = () => {
      const width = el.clientWidth;
      const weeks = Math.max(8, Math.floor((width + CELL_GAP) / (CELL_SIZE + CELL_GAP)));
      setVisibleWeeks(Math.min(weeks, MAX_WEEKS));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (prefersReducedMotion) return;
    const id = setInterval(() => {
      counterRef.current += 1;
      setEntries((prev) => [generateEntry(counterRef.current), ...prev.slice(0, 5)]);
    }, 4000);
    return () => clearInterval(id);
  }, [prefersReducedMotion]);

  // Offset grid cells so we always show the most recent data
  const cellOffset = (MAX_WEEKS - visibleWeeks) * DAYS;

  // Custom tooltip state — position relative to grid container
  const [tooltip, setTooltip] = useState<{ text: string; x: number; y: number } | null>(null);

  return (
    <div className="mt-8 mb-6">
      <div className="text-[10px] font-[family-name:var(--font-mono)] tracking-widest uppercase text-[var(--color-fg-tertiary)] mb-4">
        Always shipping
      </div>

      <div className="border border-[var(--color-border)] bg-[var(--color-surface)] overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-[var(--color-border)]">
          {/* Left: description + contribution grid */}
          <div className="p-5 md:p-6 flex flex-col justify-center gap-4 relative">
            <div>
              <h4 className="text-sm font-medium mb-2">AI agents that ship 24/7</h4>
              <p className="text-xs text-[var(--color-fg-secondary)] leading-relaxed">
                Our AI development team works around the clock. Designing, building, testing, and deploying across every time zone.
              </p>
            </div>
            <span className="absolute top-5 right-5 md:right-6 text-[9px] font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)] text-right">
              No days off
            </span>
            <div ref={gridContainerRef} className="w-full relative">
              <div className="flex gap-[2px] justify-end">
                {Array.from({ length: visibleWeeks }, (_, w) => (
                  <div key={w} className="flex flex-col gap-[2px]">
                    {Array.from({ length: DAYS }, (_, d) => {
                      const idx = cellOffset + w * DAYS + d;
                      const cell = GRID_CELLS[idx]!;
                      return (
                        <div
                          key={d}
                          className="w-[10px] h-[10px] rounded-[2px] cursor-default"
                          onMouseEnter={(e) => {
                            const el = e.target as HTMLElement;
                            const container = gridContainerRef.current;
                            if (!container) return;
                            const cRect = container.getBoundingClientRect();
                            const eRect = el.getBoundingClientRect();
                            setTooltip({
                              text: `${cell.tasks} agent tasks on ${cell.dateStr}`,
                              x: eRect.left - cRect.left + eRect.width / 2,
                              y: eRect.top - cRect.top,
                            });
                          }}
                          onMouseLeave={() => setTooltip(null)}
                          style={{
                            backgroundColor: cell.intensity > 0.6
                              ? "#216e39"
                              : cell.intensity > 0.4
                                ? "#30a14e"
                                : cell.intensity > 0.25
                                  ? "#40c463"
                                  : "#9be9a8",
                            opacity: 0.8,
                          }}
                        />
                      );
                    })}
                  </div>
                ))}
              </div>
              {tooltip && (() => {
                const containerW = gridContainerRef.current?.clientWidth ?? 400;
                const align = tooltip.x < 80 ? "left" : tooltip.x > containerW - 80 ? "right" : "center";
                return (
                  <div
                    className="absolute z-[100] pointer-events-none px-2.5 py-1.5 text-[10px] font-[family-name:var(--font-mono)] text-white bg-[#1b1f23] border border-white/10 rounded-md whitespace-nowrap"
                    style={{
                      left: align === "right" ? undefined : tooltip.x,
                      right: align === "right" ? containerW - tooltip.x : undefined,
                      top: tooltip.y - 8,
                      transform: align === "center" ? "translate(-50%, -100%)" : "translateY(-100%)",
                    }}
                  >
                    {tooltip.text}
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Right: activity feed */}
          <div className="p-5 md:p-6">
            <table className="w-full">
              <tbody>
                {entries.map((entry, i) => (
                  <tr
                    key={entry.id}
                    className="text-[11px] font-[family-name:var(--font-mono)]"
                    style={{
                      animation: !prefersReducedMotion && i === 0 ? "feedFadeIn 0.5s ease-out" : undefined,
                    }}
                  >
                    <td className="text-[var(--color-fg-tertiary)] tabular-nums pr-4 py-1.5 whitespace-nowrap align-middle">
                      {entry.time}
                    </td>
                    <td className="text-[var(--color-fg-secondary)] pr-4 py-1.5 whitespace-nowrap align-middle">
                      {entry.agent}
                    </td>
                    <td className="text-[var(--color-fg-tertiary)] py-1.5 whitespace-nowrap align-middle">
                      {entry.action}
                    </td>
                    <td className="text-emerald-500 pl-4 py-1.5 whitespace-nowrap align-middle text-right">
                      ✓
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>


      <style>{`
        @keyframes feedFadeIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
